import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { LoadingSpinner } from "adminComponents/atoms/LoadingSpinner";
import { TeacherHomepageImproved } from "adminComponents/screens/TeacherHomepageImproved";
import { useErrorToast } from "adminComponents/utils/toast";
import { useAnalytics, usePageTrack } from "lib/contexts/analytics";
import { usePageTitle } from "lib/hooks/usePageTitle";
import { useAuth } from "links/lib/features/auth";
import {
  useFetchClassroomStudents,
  useFetchClassrooms,
} from "links/lib/features/classrooms";
import {
  useFetchMostCopiedCreators,
  useFetchPracticeSetsFromFollowed,
  useFetchRecommendedPracticeSets,
} from "links/lib/features/practiceSets";
import { useFetchIndividualSessionsCount } from "links/lib/features/sessions";
import { useFetchSocialEventReactionsCount } from "links/lib/features/social";
import { useGetClassroomLiveBanners } from "links/lib/hooks/useGetClassroomLiveBanners";
import {
  AnalyticsEvent,
  AnalyticsPage,
  IClassroom,
  IPracticeSetSession,
} from "links/lib/types";
import { useGetRecentlyViewedClassrooms } from "screens/TeacherClassrooms/shared/hooks/useGetRecentlyViewedClassrooms";
import { useNavigationData } from "screens/TeacherDashboard/contexts/TeacherNavigationDataProvider";

const TeacherHome: React.FC = () => {
  const { authUser } = useAuth();
  const history = useHistory();
  const { navigationData } = useNavigationData();
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation("admin", {
    useSuspense: false,
    keyPrefix: "teacherHomeContainer",
  });

  usePageTitle(t("pageTitle"));
  usePageTrack(AnalyticsPage.TeacherDashboard_Home);

  const fetchRecommendedPracticeSets = useFetchRecommendedPracticeSets({
    periodDays: 30,
    limit: 11,
  });
  useErrorToast(fetchRecommendedPracticeSets.error);

  const fetchMostCopiedCreators = useFetchMostCopiedCreators({
    periodDays: 30,
  });
  useErrorToast(fetchMostCopiedCreators.error);

  const fetchPracticeSetsFromFollowed = useFetchPracticeSetsFromFollowed({});
  useErrorToast(fetchPracticeSetsFromFollowed.error);

  const fetchSocialEventReactionsCount = useFetchSocialEventReactionsCount({});
  useErrorToast(fetchSocialEventReactionsCount.error);

  const fetchIndividualSessionsCount = useFetchIndividualSessionsCount({});
  useErrorToast(fetchIndividualSessionsCount.error);

  const isLoading =
    fetchRecommendedPracticeSets.isLoading ||
    fetchMostCopiedCreators.isLoading ||
    fetchPracticeSetsFromFollowed.isLoading ||
    fetchSocialEventReactionsCount.isLoading ||
    fetchIndividualSessionsCount.isLoading;

  const fetchClassrooms = useFetchClassrooms({
    archived: false,
  });
  const recentlyViewedClassrooms = useGetRecentlyViewedClassrooms({
    classrooms: fetchClassrooms.data?.classrooms || [],
    limit: 2,
  });

  useEffect(() => {
    if (recentlyViewedClassrooms.length) {
      setSelectedClassroom(recentlyViewedClassrooms[0]);
    }
  }, [recentlyViewedClassrooms]);

  const [selectedClassroom, setSelectedClassroom] = useState<
    IClassroom | undefined
  >(recentlyViewedClassrooms[0]);

  const fetchSelectedClassroomStudents = useFetchClassroomStudents({
    classroom_id: selectedClassroom?.id,
  });

  useErrorToast(fetchClassrooms.error);
  useErrorToast(fetchSelectedClassroomStudents.error);

  const liveBanners = useGetClassroomLiveBanners({ showClassroomName: true });

  const handleJoinSession = (session: IPracticeSetSession) => {
    history.push(`/session/join/${session.join_code}`);

    trackEvent(
      AnalyticsEvent.TeacherDashboard_Homepage_LiveBanner_JoinSession,
      {
        sessionId: session.id,
      }
    );
  };

  if (!authUser) {
    return <LoadingSpinner />;
  }

  return (
    <TeacherHomepageImproved
      navigationData={navigationData}
      authUser={authUser}
      isLoading={isLoading}
      socialEventReactionsCount={
        fetchSocialEventReactionsCount.data?.social_event_reactions_count || "0"
      }
      prevWindowSocialEventReactionsCount={
        fetchSocialEventReactionsCount.data
          ?.prev_window_social_event_reactions_count || "0"
      }
      individualSessionsCount={
        (fetchIndividualSessionsCount.data?.individual_sessions_count || 0) +
        (fetchIndividualSessionsCount.data?.remix_sessions_count || 0)
      }
      prevWindowIndividualSessionsCount={
        (fetchIndividualSessionsCount.data
          ?.prev_window_individual_sessions_count || 0) +
        (fetchIndividualSessionsCount.data?.prev_window_remix_sessions_count ||
          0)
      }
      recommendedPracticeSets={
        fetchRecommendedPracticeSets.data?.practice_sets || []
      }
      mostCopiedCreators={fetchMostCopiedCreators.data?.most_copied_users || []}
      newSetsFromFollowed={
        fetchPracticeSetsFromFollowed.data?.practice_sets || []
      }
      liveBanners={liveBanners}
      handleJoinSession={handleJoinSession}
    />
  );
};

export default TeacherHome;
